/*
 * tp-explorer-core.js — Rechenkern der Preis-Leistungs-Karte, ohne DOM.
 *
 * Hier steht, was ein Angebot wert ist (score/wert), was ein Filter durchlaesst (pass)
 * und was auf der besten Linie liegt (pareto/genLine). Kein Zugriff auf document —
 * dieselbe Datei laeuft im Browser (window.TPExplorerCore) und unter Node
 * (module.exports, siehe scripts/tp-explorer-core.test.mjs).
 *
 * Getrennt von tp-explorer.js, damit Seiten, die nur eine Bestenliste zeigen
 * (snippets/tp-ex-best.liquid), 6 KB laden statt 32 KB. Wer hier etwas aendert,
 * aendert beide Anzeigen — genau das ist der Zweck: es gibt nur eine Rechnung.
 */
(function () {
  'use strict';

  /* ======================= Kern (ohne DOM) ======================= */

  var GI = { C: 0, B: 1, A: 2, N: 3 };
  var GLABEL = { N: 'Neu', A: 'Wie neu', B: 'Sehr gut', C: 'Gut' };
  var GCLASS = { N: 'g-n', A: 'g-a', B: 'g-b', C: 'g-c' };
  var GSTEP = { N: 1, A: 2, B: 3, C: 4 };   // Position in der Zustands-Segmentleiste
  var GRADES = ['N', 'A', 'B', 'C'];
  var WKEYS = ['cam', 'batt', 'chip', 'recency', 'updates', 'storage'];
  var WLABEL = { cam: 'Kamera', batt: 'Akku', chip: 'Leistung', recency: 'Modelljahr', updates: 'Sicherheitsupdates', storage: 'Speicher' };
  var PRESETS = [
    { l: 'Ausgewogen', w: { cam: 50, batt: 50, chip: 50, recency: 50, updates: 50, storage: 50 } },
    { l: 'Kamera zuerst', w: { cam: 100, batt: 40, chip: 40, recency: 45, updates: 30, storage: 35 } },
    { l: 'Lange nutzen', w: { cam: 40, batt: 55, chip: 45, recency: 85, updates: 100, storage: 40 } },
    { l: 'Günstig und solide', w: { cam: 45, batt: 65, chip: 45, recency: 35, updates: 50, storage: 15 } }
  ];
  var STOS = [0, 64, 128, 256, 512];
  var UPDS = [0, 2, 4, 6];
  /* Szenarien („Ich bin …"): ein Klick legt mehrere Filter UND die Regler. Was im
     q-Objekt fehlt, bleibt auf der Ausgangslage — ein Szenario ist eine Abweichung
     vom Standard, kein zweiter Standard. p = Index in PRESETS (null: Regler bleiben). */
  var SCEN = [
    { id: 'spar', l: 'Sparfuchs', s2: 'Wenig zahlen, viel bekommen', q: { pmin: 100, pmax: 350, upd: 2 }, p: 0 },
    { id: 'foto', l: 'Viel fotografieren', s2: 'Kamera zählt am meisten', q: { gr: 'B' }, p: 1 },
    { id: 'lang', l: 'Lange nutzen', s2: 'Updates und Akku zuerst', q: { upd: 4 }, p: 2 },
    { id: 'apple', l: 'Beim iPhone bleiben', s2: 'Nur Apple, günstigster Einstieg', q: { brand: 'apple', fams: [] }, p: 0 },
    { id: 'kind', l: 'Fürs Kind', s2: 'Robust, günstig, noch Updates', q: { pmin: 80, pmax: 250, gr: 'C', upd: 2, sto: 64 }, p: null },
    {
      id: 'flag', l: 'Flaggschiff gebraucht', s2: 'Oberklasse von gestern', p: 0,
      /* Baureihen ueber Markengrenzen hinweg — deshalb brand: null. */
      q: { brand: null, fams: ['iphone-pro', 'iphone-pro-max', 'galaxy-ultra', 'pixel-pro'], gr: 'B' }
    }
  ];
  /* Lesbare Namen fuer die Familien aus dem JSON. Unbekannte Familie -> aus der id
     gebaut, damit ein neuer Wert im Backend die Chips nicht leer laesst. */
  var FAMLABEL = {
    'iphone': 'iPhone', 'iphone-pro': 'iPhone Pro', 'iphone-pro-max': 'iPhone Pro Max',
    'iphone-e-se': 'iPhone SE & e', 'pixel': 'Pixel', 'pixel-pro': 'Pixel Pro', 'pixel-a': 'Pixel a',
    'galaxy-a': 'Galaxy A', 'galaxy-s': 'Galaxy S', 'galaxy-s-plus': 'Galaxy S+',
    'galaxy-ultra': 'Galaxy Ultra', 'galaxy-z': 'Galaxy Z',
    'xiaomi': 'Xiaomi', 'sonstige': 'Sonstige'
  };
  /* Marke ist die erste Stufe, Baureihe die zweite: „iPhone" allein zeigt sonst nur ein
     Drittel der Apple-Angebote, weil Pro und Pro Max eigene Familien sind. */
  var BRANDS = ['apple', 'samsung', 'google', 'xiaomi', 'sonstige'];
  var BRANDLABEL = { apple: 'Apple', samsung: 'Samsung', google: 'Google', xiaomi: 'Xiaomi', sonstige: 'Sonstige' };
  /* Feste, lesbare Reihenfolge der Baureihen-Chips — nicht nach Angebotszahl, die springt. */
  var FAMORDER = [
    'iphone', 'iphone-pro', 'iphone-pro-max', 'iphone-e-se',
    'galaxy-s', 'galaxy-s-plus', 'galaxy-ultra', 'galaxy-z', 'galaxy-a',
    'pixel', 'pixel-a', 'pixel-pro', 'xiaomi', 'sonstige'
  ];
  /* Linienfarben als Tokens, nicht als Hex: auf Weiss braucht dieselbe Linie einen
     dunkleren Ton (siehe --ex-f* in tp-explorer.css). */
  var FAMCOL = ['var(--ex-f0)', 'var(--ex-f1)', 'var(--ex-f2)', 'var(--ex-f3)', 'var(--ex-f4)', 'var(--ex-f5)'];
  var COLORNAME = {
    black: 'Schwarz', white: 'Weiß', blue: 'Blau', green: 'Grün', red: 'Rot', purple: 'Lila',
    gold: 'Gold', silver: 'Silber', gray: 'Grau', pink: 'Rosa', yellow: 'Gelb',
    orange: 'Orange', titanium: 'Titan', brown: 'Braun', natural: 'Naturton'
  };

  function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }

  function famLabel(id) {
    if (FAMLABEL[id]) { return FAMLABEL[id]; }
    return String(id).split('-').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
  }
  /* Marken-Schluessel: die vier grossen namentlich, alles andere unter „Sonstige". */
  function brandKey(m) {
    var b = String(m.brand || '').toLowerCase();
    return (BRANDS.indexOf(b) > -1 && b !== 'sonstige') ? b : 'sonstige';
  }
  function famSort(a, b) {
    var i = FAMORDER.indexOf(a), j = FAMORDER.indexOf(b);
    return (i < 0 ? 99 : i) - (j < 0 ? 99 : j) || (a < b ? -1 : (a > b ? 1 : 0));
  }
  /* Kurzname fuer die Knoten der Generationsleiter: „iPhone 14 Pro" -> „14 Pro". */
  function shortName(m) {
    var s = String(m.name || ''), head = famLabel(m.family).split(' ')[0];
    s = s.replace(new RegExp('^' + m.brand + '\\s+', 'i'), '');
    if (head !== 'Sonstige') { s = s.replace(new RegExp('^' + head + '\\s+', 'i'), ''); }
    return s || m.name;
  }

  /* Flache Punktliste. Reihenfolge = DOM-Reihenfolge der Kreise. */
  function build(data) {
    var byB = {}, i, pts = [];
    var now = data.now_year || new Date().getFullYear();
    for (i = 0; i < data.models.length; i++) { byB[data.models[i].b] = data.models[i]; }
    for (i = 0; i < data.offers.length; i++) {
      var o = data.offers[i], m = byB[o.b];
      if (!m) { continue; }   // ohne Stammdaten kein Punkt (D5: die landen in unknown[])
      var cols = (o.colors && o.colors.length) ? o.colors : null;
      /* Abgelaufene Updates: zaehlen im Score als 0 und sind von der besten Linie und der Kaufliste
         ausgeschlossen (sie bleiben aber als hohle Punkte auf der Karte). */
      var exp = m.security_until < now;
      pts.push({
        i: pts.length, m: m, st: o.st, gr: o.gr, h: o.h, img: o.img,
        price: o.price, qty: o.qty, cols: cols, exp: exp,
        act: null, px: o.price, pq: o.qty, v: 0, ok: true,
        n: {
          cam: (m.cam - 1) / 4, batt: (m.batt - 1) / 4, chip: (m.chip - 1) / 4,
          recency: clamp01((m.release_year - 2020) / 6),
          updates: exp ? 0 : clamp01((m.security_until - now) / 6),
          storage: clamp01((Math.log(o.st / 64) / Math.LN2) / 4)
        }
      });
    }
    return pts;
  }

  /* Die guenstigste Farbvariante (oder die guenstigste der gewaehlten Grundfarbe).
     Ohne colors[] ist das Angebot selbst die Variante. */
  function active(p, base) {
    if (!p.cols) { return base ? null : { h: p.h, price: p.price, qty: p.qty, img: p.img, base: null }; }
    var best = null;
    for (var i = 0; i < p.cols.length; i++) {
      var c = p.cols[i];
      if (base && c.base !== base) { continue; }
      if (!best || c.price < best.price) { best = c; }
    }
    return best;
  }

  function wert(p, w) {
    var s = 0, t = 0;
    for (var i = 0; i < WKEYS.length; i++) { var k = WKEYS[i]; s += (w[k] || 0) * p.n[k]; t += (w[k] || 0); }
    return t ? 100 * s / t : 0;
  }

  /* „Muss"-Bedingungen, hart und mit UND (D7). Setzt nebenbei die aktive Farbvariante,
     weil Preis und Bestand des Punktes an ihr haengen. */
  function pass(p, q) {
    var a = active(p, q.color);
    p.act = a;
    if (!a) { return false; }
    p.px = a.price; p.pq = a.qty;
    return (!q.brand || brandKey(p.m) === q.brand)
      && (!q.fams || !q.fams.length || q.fams.indexOf(p.m.family) > -1)
      && p.st >= q.sto
      && GI[p.gr] >= GI[q.gr]
      /* Zustandsart steht senkrecht auf dem Zustand: gr ist eine MINDESTqualität und
         kann Neuware nicht ausschließen („Gut" lässt alles durch). cond sagt, welche
         Art überhaupt gemeint ist — beides zusammen ergibt „generalüberholt, mindestens
         Wie neu". Neuware ist genau gr === 'N'. */
      && (!q.cond || (q.cond === 'neu' ? p.gr === 'N' : p.gr !== 'N'))
      && a.price >= (q.pmin || 0) * 100
      && a.price <= q.pmax * 100
      && (!q.upd || (p.m.security_until - q.now) >= q.upd);
  }

  /* Wert EINMAL je Punkt und Reglerstand nach p.v schreiben. Vorher rief jeder
     Sortiervergleich wert() erneut auf — bei 330 Punkten sind das ~30.000 Multiplikationen
     je Regler-Tick fuer eine Zahl, die schon dastand. pareto() und genLine() lesen
     seitdem p.v und setzen voraus, dass score() vorher lief. */
  function score(pts, w) {
    for (var i = 0; i < pts.length; i++) { pts[i].v = wert(pts[i], w); }
    return pts;
  }

  /* Pareto-Front: nach Preis aufsteigend, Gesamtnote absteigend — wer den bisherigen
     Hoechstwert schlaegt, liegt auf der besten Linie. Abgelaufene Updates und alles
     unter minV bleiben draussen: das ist Alt-Katalog, kein Einstiegstipp.
     Setzt score() voraus. */
  function pareto(pts, minV) {
    var lo = minV || 0;
    var a = pts.filter(function (p) { return !p.exp && p.v >= lo; })
      .sort(function (x, y) { return x.px - y.px || y.v - x.v; });
    var top = -Infinity, out = [];
    for (var i = 0; i < a.length; i++) {
      if (a[i].v > top) { top = a[i].v; out.push(a[i]); }
    }
    return out;
  }

  /* Generationsleiter je Familie: genau EIN Punkt je Modell (das guenstigste passende
     Angebot, bei Preisgleichstand das mit dem hoeheren Wert), Reihenfolge nach
     Release-Jahr. Vorher war es ein Punkt je Jahr quer ueber alle Modelle — das gab
     das Zickzack, weil „Pro" und Basismodell im selben Jahr wechselten. */
  function genLine(pts, fam) {
    var by = {}, ks = [], i;
    for (i = 0; i < pts.length; i++) {
      var p = pts[i];
      if (p.m.family !== fam) { continue; }
      var cur = by[p.m.b];
      if (!cur) { ks.push(p.m.b); by[p.m.b] = p; }
      else if (p.px < cur.px || (p.px === cur.px && p.v > cur.v)) { by[p.m.b] = p; }
    }
    return ks.map(function (k) { return by[k]; })
      .sort(function (a, b) { return a.m.release_year - b.m.release_year || a.px - b.px; });
  }

  function copyQ(q) {
    var out = {}, k;
    for (k in q) { if (q.hasOwnProperty(k)) { out[k] = q[k]; } }
    out.fams = (q.fams || []).slice();
    return out;
  }
  /* Szenario auf die Ausgangslage legen. s === null gibt genau die Ausgangslage
     zurueck — damit ist „Chip nochmal klicken" derselbe Codepfad wie „Filter lockern". */
  function applyScen(s, q0, w0) {
    var q = copyQ(q0), w = {}, k;
    WKEYS.forEach(function (x) { w[x] = w0[x]; });
    if (!s) { return { q: q, w: w }; }
    for (k in s.q) { if (s.q.hasOwnProperty(k)) { q[k] = (k === 'fams') ? s.q[k].slice() : s.q[k]; } }
    if (s.p != null) { WKEYS.forEach(function (x) { w[x] = PRESETS[s.p].w[x]; }); }
    return { q: q, w: w };
  }
  /* Wieviele Feinfilter weichen von der Ausgangslage ab? Steht als Zahl am Klappbereich. */
  function fineCount(q, q0) {
    return ['sto', 'gr', 'upd', 'color', 'cond'].filter(function (k) { return q[k] !== q0[k]; }).length;
  }

  /* Hash-Felder: 0 Baureihen (mit + verbunden), 1 Speicher, 2 Zustand, 3 Hoechstpreis,
     4 Updates, 5 Farbe, 6 Marke, 7 Mindestpreis. Feld 6/7 sind neu — alte Links (eine
     Baureihe, kein Feld 6/7) werden weiter gelesen, die Marke kommt dann aus der Familie. */
  function hashOf(q) {
    var f = [
      (q.fams && q.fams.length) ? q.fams.join('+') : 'alle',
      q.sto, q.gr, q.pmax, q.upd, q.color || 'alle', q.brand || 'alle', q.pmin || 0
    ];
    /* Das neunte Feld steht nur da, wenn es etwas sagt. Fehlt es, liest parseHash
       „alle" — so bleiben die kurzen Links von früher gültig und die heutigen kurz. */
    if (q.cond) { f.push(q.cond); }
    return 'e=' + f.join(',');
  }

  function parseHash(h, fams, bases, pmax, famBrand) {
    var m = /(?:^|[#&])e=([^&]+)/.exec(h || '');
    if (!m) { return null; }
    var a = decodeURIComponent(m[1]).split(','), q = {}, f0 = a[0] || 'alle';
    q.fams = (f0 === 'alle') ? [] : f0.split('+').filter(function (f) { return fams.indexOf(f) > -1; });
    q.sto = STOS.indexOf(+a[1]) > -1 ? +a[1] : 0;
    q.gr = GI[a[2]] !== undefined ? a[2] : 'C';
    q.pmax = (+a[3] > 0 && +a[3] <= pmax) ? +a[3] : pmax;
    q.upd = UPDS.indexOf(+a[4]) > -1 ? +a[4] : null;
    q.color = (a[5] && bases.indexOf(a[5]) > -1) ? a[5] : null;
    q.brand = BRANDS.indexOf(a[6]) > -1 ? a[6] : null;
    /* Eine ausgeschriebene 0 ist eine Ansage, kein fehlender Wert: „ab 0 €" muss den
       Section-Standardmindestpreis überstimmen können. Sonst landet jeder, der von einer
       Seite wie „Smartphones bis 200 €" herkommt, bei „ab 100 €" und sieht die günstige
       Hälfte nicht — und wer auf der Karte selbst den Regler auf 0 zieht, bekommt beim
       nächsten Aufruf seines eigenen Links wieder 100. Fehlt das Feld (ältere Links mit
       sieben Feldern), bleibt es beim Standard. */
    q.pmin = (a[7] !== undefined && a[7] !== '' && +a[7] >= 0) ? +a[7] : null;
    q.cond = (a[8] === 'neu' || a[8] === 'refurb') ? a[8] : null;
    /* #e=apple: nur die Marke im Link. */
    if (!q.brand && BRANDS.indexOf(f0) > -1) { q.brand = f0; q.fams = []; }
    /* Marke nur ableiten, wenn ALLE Baureihen zur selben gehoeren — das Szenario
       „Flaggschiff guenstig" mischt Apple, Samsung und Google in einem Link. */
    if (!q.brand && q.fams.length && famBrand) {
      var bs = q.fams.map(function (f) { return famBrand[f]; });
      q.brand = bs.every(function (b) { return b && b === bs[0]; }) ? bs[0] : null;
    }
    if (q.brand) { q.fams = q.fams.filter(function (f) { return !famBrand || famBrand[f] === q.brand; }); }
    return q;
  }

  var core = {
    GI: GI, GLABEL: GLABEL, GCLASS: GCLASS, GSTEP: GSTEP,
    GRADES: GRADES, WKEYS: WKEYS, WLABEL: WLABEL, PRESETS: PRESETS,
    STOS: STOS, UPDS: UPDS, SCEN: SCEN, FAMLABEL: FAMLABEL,
    BRANDS: BRANDS, BRANDLABEL: BRANDLABEL, FAMORDER: FAMORDER, FAMCOL: FAMCOL,
    COLORNAME: COLORNAME, clamp01: clamp01, famLabel: famLabel, brandKey: brandKey,
    famSort: famSort, shortName: shortName, build: build, active: active,
    wert: wert, pass: pass, score: score, pareto: pareto,
    genLine: genLine, copyQ: copyQ, applyScen: applyScen, fineCount: fineCount,
    hashOf: hashOf, parseHash: parseHash
  };
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  window.TPExplorerCore = core;
}());
